import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
// import { AuthContext } from "@/context/Auth";
// import { GlobalContext } from "@/context/Global";

import PrivateRoute from "./PrivateRoutes";
import PublicRoute from "./PublicRoutes";
import { PublicWrapper } from "@/components/PublicWrapper";
import { NotFoundPage } from "@/pages/404";
import { SnackBar } from "@/components/SnackBar";
import { SessionExpiredModal } from "@/components/SessionExpiredModal";

// generatePagesRoutes
import { AdminWrapper } from "@/components/AdminWrapper";

import {
  AdminForgotPage,
  AdminLoginPage,
  AdminProfilePage,
  AdminResetPage,
  ListAdminWireframeTablePage,
  AdminSignUpPage,
  CustomerLogin,
  CustomerDashboard,
  CustomerReports,
  CustomerReport,
  CustomerWebsiteAnalysis,
  CustomerSettings,
  CustomerRegister,
  CustomerOauth,
  CustomerReset,
  ListAdminUserPage,
  ListAdminPlansPage,
  ViewAdminUserTable,
  ViewAdminPricePage,
  ViewAdminPlanPage,
  EditAdminUserTablePage,
  Success,
  EditAdminPlanPage,
  ListAdminPricePage,
} from "./LazyLoad";

import EditWireframePage from "@/pages/Admin/Edit/EditWireframePage";
import { useContexts } from "@/hooks/useContexts";
import { RoleEnum } from "@/utils/Enums";
import { LazyLoad } from "@/components/LazyLoad";
import { RouteChangeModal } from "@/components/RouteChangeModal";
import CustomerForgotPage from "@/pages/Customer/Auth/CustomerForgotPage";
import MagicLoginVerifyPage from "@/pages/MagicLogin/MagicLoginVerifyPage";

import ViewAdminPriceDetailPage from "@/pages/Admin/View/ViewAdminPriceDetailPage";
import EditAdminPricePage from "@/pages/Admin/Edit/EditAdminPricePage";
import AddAdminPricePage from "@/pages/Admin/Add/AddAdminPricePage";
import { useNavigate } from "react-router-dom";

export interface DynamicWrapperProps {
  isAuthenticated?: boolean;
  role?: RoleEnum;
  children: React.ReactNode;
}

export const DynamicWrapper: React.FC<DynamicWrapperProps> = ({
  isAuthenticated,
  role,
  children,
}) => {
  if (!isAuthenticated) {
    return <PublicWrapper>{children}</PublicWrapper>;
  }
  if (isAuthenticated) {
    if (role && [RoleEnum.ADMIN, RoleEnum.SUPER_ADMIN].includes(role)) {
      return <AdminWrapper>{children}</AdminWrapper>;
    }
  }
};

export interface NotFoundProps {
  isAuthenticated?: boolean;
  role?: RoleEnum | null;
}

export const NotFound: React.FC<NotFoundProps> = ({
  isAuthenticated,
  role,
}) => {
  if (!isAuthenticated) {
    return (
      <PublicWrapper>
        <NotFoundPage />
      </PublicWrapper>
    );
  }
  if (isAuthenticated) {
    if (role && [RoleEnum.ADMIN, RoleEnum.SUPER_ADMIN].includes(role)) {
      return (
        <AdminWrapper>
          <NotFoundPage />
        </AdminWrapper>
      );
    }
  }
};

export default () => {
  const {
    globalState,
    globalDispatch: dispatch,
    authState: state,
    setGlobalState,
  } = useContexts();
  const navigate = useNavigate();

  const isOpen = globalState?.isOpen ?? false;
  const openRouteChangeModal = globalState?.openRouteChangeModal ?? false;

  const [screenSize, setScreenSize] = useState(window.innerWidth);

  // function setDimension(e: Event) {
  //   const target = e.currentTarget as Window;
  //   if (target.innerWidth >= 1024) {
  //     toggleSideBar(true);
  //   } else toggleSideBar(false);
  //   setScreenSize(target.innerWidth);
  // }

  // const toTop = () => {
  //   containerRef.current.scrollTo(0, 0);
  // };

  const portalChange = useCallback(
    (e: any) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        e.shiftKey &&
        e.altKey &&
        ["r", "R"].includes(e.key)
      ) {
        setGlobalState("openRouteChangeModal", true);
      }
      // console.log("PORTAL CHANGE  >>", openRouteChangeModal, e);
      if (["Escape", "escape", "ESCAPE", "Esc", "esc"].includes(e.key)) {
        setGlobalState("openRouteChangeModal", false);
      }
    },
    [setGlobalState]
  );

  const toggleSideBar = (open: boolean) => {
    if (isOpen && screenSize < 1024) {
      dispatch({
        type: "OPEN_SIDEBAR",
        payload: { isOpen: open },
      });
    } else if (!isOpen && screenSize >= 1024) {
      dispatch({
        type: "OPEN_SIDEBAR",
        payload: { isOpen: open },
      });
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    window.addEventListener(
      "resize",
      (e) => {
        const target = e.currentTarget as Window;
        if (target.innerWidth >= 1024) {
          toggleSideBar(true);
        } else toggleSideBar(false);
        setScreenSize(target.innerWidth);
      },
      { signal }
    );

    return () => {
      controller.abort();
    };
  }, [screenSize]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    window.addEventListener("keydown", portalChange, { signal });

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <div
      onClick={() => {
        isOpen ? toggleSideBar(false) : null;
      }}
      className={`h-svh grid grid-cols-1 grid-rows-[auto_1fr] min-h-svh max-h-svh overflow-y-hidde overflow-x-hidden bg-white`}
    >
      <Routes>
        <Route
          path="/admin/profile"
          element={
            <PrivateRoute
              access={"admin"}
              path={"/admin/profile"}
              element={
                <AdminWrapper>
                  <AdminProfilePage />
                </AdminWrapper>
              }
            />
          }
        />

        <Route
          path="/admin/edit-wireframe/:id"
          element={
            <PrivateRoute
              access={["admin", "super_admin"]}
              path={"/admin/edit-wireframe/:id"}
              element={
                <AdminWrapper>
                  <EditWireframePage />
                </AdminWrapper>
              }
            />
          }
        />

        <Route
          path="/admin/users/edit/:id"
          element={
            <PrivateRoute
              access={["admin", "super_admin"]}
              path={"/admin/users/edit/:id"}
              element={
                <AdminWrapper>
                  <EditAdminUserTablePage />
                </AdminWrapper>
              }
            />
          }
        />
        <Route
          path="/admin/users/view/:id"
          element={
            <PrivateRoute
              access={["admin", "super_admin"]}
              path={"/admin/users/view/:id"}
              element={
                <AdminWrapper>
                  <ViewAdminUserTable />
                </AdminWrapper>
              }
            />
          }
        />

        <Route
          path="/admin/users"
          element={
            <PrivateRoute
              access={["admin", "super_admin"]}
              path={"/admin/users"}
              element={
                <AdminWrapper>
                  <ListAdminUserPage />
                </AdminWrapper>
              }
            />
          }
        />
        {/* ViewAdminPricePage */}
        <Route
          path="/admin/prices"
          element={
            <PrivateRoute
              access={["admin", "super_admin"]}
              path={"/admin/prices"}
              element={
                <AdminWrapper>
                  <ListAdminPricePage />
                </AdminWrapper>
              }
            />
          }
        />

        <Route
          path="/admin/plans"
          element={
            <PrivateRoute
              access={["admin", "super_admin"]}
              path={"/admin/plans"}
              element={
                <AdminWrapper>
                  <ListAdminPlansPage />
                </AdminWrapper>
              }
            />
          }
        />

        <Route
          path="/admin/build"
          element={
            <PrivateRoute
              access={["admin", "super_admin"]}
              path={"/admin/build"}
              element={
                <AdminWrapper>
                  <ListAdminWireframeTablePage />
                </AdminWrapper>
              }
            />
          }
        />

        <Route
          path="/admin/login"
          element={
            <PublicRoute
              path={"/admin/login"}
              element={
                <PublicWrapper>
                  <AdminLoginPage />
                </PublicWrapper>
              }
            />
          }
        />

        <Route
          path="/admin/sign-up"
          element={
            <PublicRoute
              path={"/admin/sign-up"}
              element={
                <PublicWrapper>
                  <AdminSignUpPage />
                </PublicWrapper>
              }
            />
          }
        />
        <Route
          path="/admin/forgot"
          element={
            <PublicRoute
              path={"/admin/forgot"}
              element={
                <PublicWrapper>
                  <AdminForgotPage />
                </PublicWrapper>
              }
            />
          }
        />
        <Route
          path="/admin/reset"
          element={
            <PublicRoute
              path={"/admin/reset"}
              element={
                <PublicWrapper>
                  <AdminResetPage />
                </PublicWrapper>
              }
            />
          }
        />

        <Route
          path="/"
          element={
            <PublicRoute
              path={"/"}
              element={
                // <PublicWrapper>
                <CustomerLogin />
                // </PublicWrapper>
              }
            />
          }
        />

        <Route
          path="/customer/login"
          element={
            <PublicRoute path={"/customer/login"} element={<CustomerLogin />} />
          }
        />
        <Route
          path="/customer/verify"
          element={
            <PublicRoute path={"/customer/verify"} element={<Success />} />
          }
        />

        <Route
          path="/verify-email"
          element={
            <PublicRoute
              path={"/verify-email"}
              element={<MagicLoginVerifyPage />}
            />
          }
        />

        <Route
          path="/login/oauth"
          element={
            <PublicRoute path={"/login/oauth"} element={<CustomerOauth />} />
          }
        />
        <Route
          path="/customer/register"
          element={
            <PublicRoute
              path={"/customer/register"}
              element={<CustomerRegister />}
            />
          }
        />
        <Route
          path="/customer/reset"
          element={
            <PublicRoute path={"/customer/reset"} element={<CustomerReset />} />
          }
        />

        <Route
          path="/customer/forgot-password"
          element={
            <PublicRoute
              path={"/customer/forgot-password"}
              element={<CustomerForgotPage />}
            />
          }
        />

        <Route
          path="/customer/settings"
          element={
            <PrivateRoute
              access={["customer"]}
              path={"/customer/settings"}
              element={<CustomerSettings />}
            />
          }
        />
        <Route
          path="/customer/dashboard"
          element={
            <PrivateRoute
              access={["customer"]}
              path={"/customer/dashboard"}
              element={<CustomerDashboard />}
            />
          }
        />

        <Route
          path="/customer/reports"
          element={
            <PrivateRoute
              access={["customer"]}
              path={"/customer/reports"}
              element={<CustomerReports />}
            />
          }
        />
        <Route
          path="/customer/report/:id"
          element={
            <PrivateRoute
              access={["customer"]}
              path={"/customer/report/:id"}
              element={<CustomerReport />}
            />
          }
        />
        <Route
          path="/customer/analyse-website"
          element={
            <PrivateRoute
              access={["customer"]}
              path={"/customer/analyse-website"}
              element={<CustomerWebsiteAnalysis />}
            />
          }
        />

        {/* Custom Routes */}

        <Route
          path="/admin/plans/view/:id"
          element={
            <PrivateRoute
              access={["admin", "super_admin"]}
              path={"/admin/plans/view/:id"}
              element={
                <AdminWrapper>
                  <ViewAdminPlanPage />
                </AdminWrapper>
              }
            />
          }
        />

        <Route
          path="/admin/plans/edit/:id"
          element={
            <PrivateRoute
              access={["admin", "super_admin"]}
              path={"/admin/plans/edit/:id"}
              element={
                <AdminWrapper>
                  <EditAdminPlanPage />
                </AdminWrapper>
              }
            />
          }
        />

        <Route
          path="/admin/prices/view/:id"
          element={
            <PrivateRoute
              access={["admin", "super_admin"]}
              path={"/admin/prices/view/:id"}
              element={
                <AdminWrapper>
                  <ViewAdminPriceDetailPage />
                </AdminWrapper>
              }
            />
          }
        />

        <Route
          path="/admin/prices/edit/:id"
          element={
            <PrivateRoute
              access={["admin", "super_admin"]}
              path={"/admin/prices/edit/:id"}
              element={
                <AdminWrapper>
                  <EditAdminPricePage />
                </AdminWrapper>
              }
            />
          }
        />

        <Route
          path="/admin/prices/add"
          element={
            <PrivateRoute
              access={["admin", "super_admin"]}
              path={"/admin/prices/add"}
              element={
                <AdminWrapper>
                  <AddAdminPricePage
                    onClose={() => navigate("/admin/prices")}
                    onSuccess={() => navigate("/admin/prices")}
                  />
                </AdminWrapper>
              }
            />
          }
        />

        <Route
          path={"*"}
          element={
            <PublicRoute
              path={"*"}
              element={
                <NotFound
                  isAuthenticated={state?.isAuthenticated}
                  role={state?.role as RoleEnum | null}
                />
              }
            />
          }
        />
      </Routes>
      <SessionExpiredModal />
      <SnackBar />

      <LazyLoad>
        <RouteChangeModal
          isOpen={openRouteChangeModal}
          onClose={() => setGlobalState("openRouteChangeModal", false)}
          options={[
            ...(state?.isAuthenticated
              ? [
                  {
                    name: `${state?.role} Login`,
                    route: `/${state?.role}/login`,
                  },
                ]
              : [
                  { name: "Admin Login", route: "/admin/login" },
                  { name: "User Login", route: "/user/login" },
                ]),
          ]}
          title="Change Route"
        />
      </LazyLoad>
    </div>
  );
};
